"use client";

import {ReportFilter} from "@/components/@filter/report-filter";
import {DownloadIcon} from "@/components/shared";
import {NeoFieldImage} from "@/components/table/neo-fields/image";
import {LoadingOverlay} from "@/components/table/neo-table/loaderNggrid";
import {useNeoContext} from "@/context/neo/neo.context";
import {downloadCSV} from "@/libs/helper";
import {formatCellValue} from "@/libs/helper/formatCellValue";
import {reortsService} from "@/service/reports.service";
import {useEffect, useMemo, useState} from "react";
import {AiOutlineFileSearch} from "react-icons/ai";
import {IoMdArrowUp} from "react-icons/io";
import {IoArrowDownOutline, IoFilterSharp} from "react-icons/io5";
import {LuChevronDown, LuChevronRight} from "react-icons/lu";
import {MdPhone} from "react-icons/md";
import {PiStudentBold} from "react-icons/pi";
import {RiCloseLine} from "react-icons/ri";

type VisitorGroupViewMode = "all" | "incampus" | "checkedout";

type TableCardDetailCardProps = {
  title: string;
  value: string;
  lines?: string[];
  icon: React.ReactNode;
  iconClassName: string;
};

type TableCardExportParams = {
  activeReport?: {menu?: string};
  downloadData: any[];
  columnUids: Record<string, string>;
  exportColumnNames: Record<string, string>;
  getVisitorTypeLabel: (value: unknown) => string;
  getPickedUpStudentsSingleLine: (value: any) => string;
  getPickedUpStudentsImageLinks: (value: any) => string;
};

type VisitorsReportTableCardProps = {
  reportdata: any;
};

const TableCardDetailCard = ({
  title,
  value,
  lines,
  icon,
  iconClassName
}: TableCardDetailCardProps) => {
  const hasMultipleLines = Boolean(lines && lines.length > 1);

  return (
    <div className="rounded-lg border border-[#e5eaf3] bg-white p-3 shadow-sm">
      <div
        className={`flex gap-3 ${hasMultipleLines ? "items-center" : "items-start"}`}
      >
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-full ${iconClassName}`}
        >
          {icon}
        </div>
        <div>
          <p className="text-xs font-medium text-default-400">{title}</p>
          {lines && lines.length > 0 ? (
            <div className="mt-1 space-y-1">
              {lines.map((line, index) => (
                <p
                  key={`${title}-${index}`}
                  className="text-xs font-semibold text-default-700"
                >
                  {line}
                </p>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-xs font-semibold text-default-700">
              {value}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const exportTableCardData = ({
  activeReport,
  downloadData,
  columnUids,
  exportColumnNames,
  getVisitorTypeLabel,
  getPickedUpStudentsSingleLine,
  getPickedUpStudentsImageLinks
}: TableCardExportParams) => {
  const fileName = `${activeReport?.menu || "table-card"}-${new Date().toISOString().slice(0, 10)}`;
  const exportData = downloadData.map((row: any) => ({
    [exportColumnNames.visitorName]: row?.[columnUids.visitorName] || "",
    "VISITOR TYPE": getVisitorTypeLabel(row?.visitorType),
    [exportColumnNames.visitorImage]: row?.[columnUids.visitorImage] || "",
    [exportColumnNames.visitorPhoneNumber]: row?.[columnUids.visitorPhoneNumber]
      ? `="${row[columnUids.visitorPhoneNumber]}"`
      : "",
    [exportColumnNames.pickedupStudents]: getPickedUpStudentsSingleLine(
      row?.[columnUids.pickedupStudents]
    ),
    "PICKED UP STUDENTS IMAGE": getPickedUpStudentsImageLinks(
      row?.[columnUids.pickedupStudents]
    ),
    [exportColumnNames.reason]:
      row?.[columnUids.reason] === "NA" ? "" : row?.[columnUids.reason] || "",
    [exportColumnNames.checkInTime]: formatCellValue.formatTimeStampCellValue(
      row?.[columnUids.checkInTime],
      "Time"
    ),
    [exportColumnNames.checkOutTime]: formatCellValue.formatTimeStampCellValue(
      row?.[columnUids.checkOutTime],
      "Time"
    ),
    DATE: formatCellValue.formatTimeStampCellValue(
      row?.[columnUids.checkInTime] || row?.[columnUids.checkOutTime],
      "Date"
    )
  }));

  downloadCSV(exportData, fileName);
};

const normalizeLabel = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const getColumnUid = (
  columns: any[] = [],
  fallbackUid: string,
  fallbackName: string
) => {
  const uidMatch = columns.find((column: any) => column?.uid === fallbackUid);
  if (uidMatch?.uid) {
    return uidMatch.uid;
  }

  const normalizedName = normalizeLabel(fallbackName);
  const nameMatch = columns.find(
    (column: any) => normalizeLabel(column?.name || "") === normalizedName
  );

  return nameMatch?.uid || fallbackUid;
};

const getColumnName = (
  columns: any[] = [],
  fallbackUid: string,
  fallbackName: string
) => {
  const uidMatch = columns.find((column: any) => column?.uid === fallbackUid);
  if (uidMatch?.name) {
    return String(uidMatch.name).toUpperCase();
  }

  const normalizedName = normalizeLabel(fallbackName);
  const nameMatch = columns.find(
    (column: any) => normalizeLabel(column?.name || "") === normalizedName
  );

  return String(nameMatch?.name || fallbackName).toUpperCase();
};

const getTodayRange = () => {
  const currentDate = new Date();
  const fromDate = new Date(currentDate);
  const toDate = new Date(currentDate);
  fromDate.setHours(0, 0, 0, 0);
  toDate.setHours(23, 59, 59, 999);

  return {
    from: fromDate.getTime(),
    to: toDate.getTime()
  };
};

const getDefaultFilters = (innerFilters: any[] = []) => {
  const filters: Record<string, any> = {};

  innerFilters.forEach((filter: any) => {
    filters[filter.uid] = filter.defaultValue || [];
  });

  return filters;
};

const buildReportRequest = ({
  innerFilters,
  offset,
  request
}: {
  innerFilters?: any[];
  offset?: string;
  request?: any;
}) => {
  const dateRange = getTodayRange();
  const defaultFilters = getDefaultFilters(innerFilters);

  return {
    filterType: request?.filterType || "1",
    from: request?.from ?? dateRange.from,
    to: request?.to ?? dateRange.to,
    offset: request?.offset ?? offset ?? "",
    staffClassId: request?.staffClassId ?? [],
    filters: {
      ...defaultFilters,
      ...(request?.filters || {})
    }
  };
};

const getPickedUpStudents = (value: any) => {
  if (!Array.isArray(value) || !value.length) {
    return "-";
  }

  return value
    .map((item: any) => {
      if (typeof item === "string") {
        return item;
      }

      const studentName = item?.studentName;
      const studentClass = item?.className;
      return studentClass
        ? `${studentName} | Class ${studentClass}`
        : studentName;
    })
    .filter(Boolean)
    .join(", ");
};

const getPickedUpStudentList = (value: any) => {
  if (!Array.isArray(value) || !value.length) {
    return [];
  }

  return value
    .map((item: any) => {
      if (typeof item === "string") {
        return item;
      }

      const studentName = item?.studentName;
      const studentClass = item?.className;

      return studentClass
        ? `${studentName} | Class ${studentClass}`
        : studentName;
    })
    .filter(Boolean);
};

const getPickedUpStudentsSingleLine = (value: any) => {
  if (!Array.isArray(value) || !value.length) {
    return "";
  }

  return value
    .map((item: any) => {
      if (typeof item === "string") {
        return item;
      }

      const studentName =
        item?.studentName || item?.name || item?.admissionNo || "";
      const studentClass = item?.className || item?.studentClass || "";

      return studentClass
        ? `${studentName} - Class ${studentClass}`
        : studentName;
    })
    .filter(Boolean)
    .join(" | ");
};

const getPickedUpStudentsImageLinks = (value: any) => {
  if (!Array.isArray(value) || !value.length) {
    return "";
  }

  return value
    .map((item: any) => {
      if (typeof item === "string") {
        return "";
      }

      return (
        item?.studentImage ||
        item?.image ||
        item?.imageUrl ||
        item?.profileImage ||
        ""
      );
    })
    .filter(Boolean)
    .join(" | ");
};

const getPickedUpStudentsSearchText = (value: any) => {
  if (!Array.isArray(value) || !value.length) {
    return "";
  }

  return value
    .map((item: any) => {
      if (typeof item === "string") {
        return item;
      }

      return `${item?.studentName || ""} ${item?.className || ""}`.trim();
    })
    .join(" ")
    .toLowerCase();
};

const getVisitorTypeLabel = (value: unknown) => {
  if (String(value) === "1") {
    return "Guardian Visitor";
  }

  if (String(value) === "2") {
    return "Walk in Visitor";
  }

  return String(value || "Walk in Visitor");
};

const hasValidTime = (value: unknown) =>
  !(
    value === null ||
    value === undefined ||
    value === "" ||
    value === 0 ||
    value === "0"
  );

const getTrimmedReason = (value: unknown, maxLength = 130) => {
  const reasonText = String(value || "NA");

  if (reasonText.length <= maxLength) {
    return reasonText;
  }

  return `${reasonText.slice(0, maxLength)}...`;
};

const VisitorDataCard = ({
  row,
  columnUids
}: {
  row: Record<string, any>;
  columnUids: Record<string, string>;
}) => {
  const reasonText = String(row?.[columnUids.reason] || "NA");
  const activityDate = formatCellValue.formatTimeStampCellValue(
    row?.[columnUids.checkInTime] || row?.[columnUids.checkOutTime],
    "Date"
  );
  const pickedUpStudents = getPickedUpStudents(
    row?.[columnUids.pickedupStudents]
  );
  const pickedUpStudentList = getPickedUpStudentList(
    row?.[columnUids.pickedupStudents]
  );
  const visitorType = getVisitorTypeLabel(row?.visitorType);
  const imageData = {
    ...row,
    [columnUids.visitorImage]:
      row?.[columnUids.visitorImage] || "/images/avatar.png"
  };

  return (
    <div className="rounded-xl border border-[#e5eaf3] bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="scale-110">
            <NeoFieldImage data={imageData} uid={columnUids.visitorImage} />
          </div>
          <div>
            <h3 className="text font-semibold leading-none text-default-700">
              {row?.[columnUids.visitorName] || "Visitor"}
            </h3>
            <p className="mt-1 text-xs text-default-500">{visitorType}</p>
          </div>
        </div>
        <p className="text-xs font-semibold text-[#3F6FB5]">{activityDate}</p>
      </div>

      <div className="mt-4 flex items-center gap-1 text-xs font-medium text-default-700">
        <MdPhone className="text-default-500" />
        <span>+{row?.[columnUids.visitorPhoneNumber] || "-"}</span>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
        {pickedUpStudents !== "-" && (
          <div className="md:col-span-2">
            <TableCardDetailCard
              title="Picked-up Students"
              value={pickedUpStudents}
              lines={pickedUpStudentList}
              icon={<PiStudentBold className="text-lg text-[#6D7EF7]" />}
              iconClassName="bg-[#EEF2FF]"
            />
          </div>
        )}
        <TableCardDetailCard
          title="Check-in"
          value={formatCellValue.formatTimeStampCellValue(
            row?.[columnUids.checkInTime],
            "Time"
          )}
          icon={<IoArrowDownOutline className="text-lg text-[#68B76E]" />}
          iconClassName="bg-[#ECF9EE]"
        />
        <TableCardDetailCard
          title="Check-out"
          value={formatCellValue.formatTimeStampCellValue(
            row?.[columnUids.checkOutTime],
            "Time"
          )}
          icon={<IoMdArrowUp className="text-lg text-[#C98686]" />}
          iconClassName="bg-[#FCEEEE]"
        />
      </div>

      <div className="group relative mt-2 rounded-2xl border border-[#E8E4DB] bg-white px-4 py-2 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
        <span className="text-xs font-semibold text-[#E2B400]">Reason:</span>
        <span className="ml-2 text-xs font-medium text-default-600">
          {getTrimmedReason(reasonText)}
        </span>
        {reasonText.length > 120 && (
          <div className="pointer-events-none absolute left-4 top-full z-20 mt-2 hidden w-60 rounded-lg border border-[#d7dbe4] bg-white px-3 py-2 text-xs font-medium text-black shadow-lg group-hover:block">
            {reasonText}
          </div>
        )}
      </div>
    </div>
  );
};

export const VisitorsReportTableCard: React.FC<
  VisitorsReportTableCardProps
> = ({reportdata}) => {
  const {filterContext, offset} = useNeoContext();
  const activeReport = reportdata?.activeReport || {};
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [downloadData, setDownloadData] = useState<any[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [groupViewMode, setGroupViewMode] =
    useState<VisitorGroupViewMode>("all");
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});
  const [reportFilters, setReportFilters] = useState<any>(() =>
    buildReportRequest({
      innerFilters: activeReport?.innerFilters,
      offset
    })
  );
  const columnUids = useMemo(
    () => ({
      visitorName: getColumnUid(
        activeReport?.columns,
        "visitorName",
        "VISITOR NAME"
      ),
      visitorImage: getColumnUid(
        activeReport?.columns,
        "visitorImage",
        "VISITOR IMAGE"
      ),
      visitorPhoneNumber: getColumnUid(
        activeReport?.columns,
        "visitorPhoneNumber",
        "PHONE NUMBER"
      ),
      pickedupStudents: getColumnUid(
        activeReport?.columns,
        "pickedupStudents",
        "PICKED UP STUDENTS"
      ),
      reason: getColumnUid(activeReport?.columns, "reason", "REASON"),
      checkInTime: getColumnUid(
        activeReport?.columns,
        "checkInTime",
        "CHECK IN TIME"
      ),
      checkOutTime: getColumnUid(
        activeReport?.columns,
        "checkOutTime",
        "CHECK OUT TIME"
      )
    }),
    [activeReport?.columns]
  );
  const exportColumnNames = useMemo(
    () => ({
      visitorName: getColumnName(
        activeReport?.columns,
        "visitorName",
        "VISITOR NAME"
      ),
      visitorImage: getColumnName(
        activeReport?.columns,
        "visitorImage",
        "VISITOR IMAGE"
      ),
      visitorPhoneNumber: getColumnName(
        activeReport?.columns,
        "visitorPhoneNumber",
        "PHONE NUMBER"
      ),
      pickedupStudents: getColumnName(
        activeReport?.columns,
        "pickedupStudents",
        "PICKED UP STUDENTS"
      ),
      reason: getColumnName(activeReport?.columns, "reason", "REASON"),
      checkInTime: getColumnName(
        activeReport?.columns,
        "checkInTime",
        "CHECK IN TIME"
      ),
      checkOutTime: getColumnName(
        activeReport?.columns,
        "checkOutTime",
        "CHECK OUT TIME"
      )
    }),
    [activeReport?.columns]
  );

  const fetchTableData = async (request: any) => {
    if (
      !activeReport?.url ||
      !filterContext?.client?.institutionUserId ||
      !filterContext?.client?.institutionId
    ) {
      return;
    }

    const normalizedRequest = buildReportRequest({
      innerFilters: activeReport?.innerFilters,
      offset,
      request
    });

    const payload = {
      userId: filterContext.client.institutionUserId,
      instituteId: filterContext.client.institutionId,
      pageNumber: 1,
      itemsPerPage: 100000,
      searchBy: "",
      sortColumn: "",
      sortValue: "",
      filterColumn: [],
      filterValue: [],
      ...normalizedRequest
    };

    setLoading(true);
    await reortsService
      .onGetReportData(activeReport.url, payload)
      .then((response: any) => {
        const reportRows = Array.isArray(response?.rows) ? response.rows : [];
        setRows(reportRows);
        setDownloadData(reportRows);
        setCurrentPage(1);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFilterSubmit = (nextFilters: any) => {
    const normalizedFilters = buildReportRequest({
      innerFilters: activeReport?.innerFilters,
      offset,
      request: nextFilters
    });

    setGroupViewMode("all");
    setReportFilters(normalizedFilters);
    setCurrentPage(1);
    setIsFilterOpen(false);
    fetchTableData(normalizedFilters);
  };

  useEffect(() => {
    if (
      activeReport?.url &&
      filterContext?.client?.institutionUserId &&
      filterContext?.client?.institutionId
    ) {
      const defaultRequest = buildReportRequest({
        innerFilters: activeReport?.innerFilters,
        offset
      });

      setReportFilters(defaultRequest);
      fetchTableData(defaultRequest);
    }
  }, [
    activeReport?.innerFilters,
    activeReport?.url,
    filterContext?.client?.institutionId,
    filterContext?.client?.institutionUserId,
    offset
  ]);

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) return rows;

    // 1. Visitor name
    let results = rows.filter((row: any) =>
      String(row?.visitorName || "")
        .toLowerCase()
        .includes(query)
    );

    if (results.length > 0) return results;

    // 2. Phone number
    results = rows.filter((row: any) =>
      String(row?.visitorPhoneNumber || "").includes(query)
    );

    if (results.length > 0) return results;

    // 3. Reason
    results = rows.filter((row: any) =>
      String(row?.reason || "")
        .toLowerCase()
        .includes(query)
    );

    if (results.length > 0) return results;

    // 4. Student name / class
    results = rows.filter((row: any) => {
      const studentText = (row?.pickedupStudents || [])
        .map(
          (student: any) =>
            `${student?.studentName || ""} ${student?.className || ""}`
        )
        .join(" ")
        .toLowerCase();

      return studentText.includes(query);
    });

    return results;
  }, [rows, searchQuery]);

  const totalVisitors = rows.length;
  const inCampus = rows.filter(
    row =>
      hasValidTime(row?.[columnUids.checkInTime]) &&
      !hasValidTime(row?.[columnUids.checkOutTime])
  ).length;
  const checkedOut = rows.filter(row =>
    hasValidTime(row?.[columnUids.checkOutTime])
  ).length;
  const groupTabs = [
    {id: "all", label: "All", count: totalVisitors},
    {id: "incampus", label: "In Campus", count: inCampus},
    {id: "checkedout", label: "Checked Out", count: checkedOut}
  ] as const;

  const groupedSourceRows = useMemo(() => {
    if (groupViewMode === "incampus") {
      return filteredRows.filter(
        row =>
          hasValidTime(row?.[columnUids.checkInTime]) &&
          !hasValidTime(row?.[columnUids.checkOutTime])
      );
    }

    if (groupViewMode === "checkedout") {
      return filteredRows.filter(row =>
        hasValidTime(row?.[columnUids.checkOutTime])
      );
    }

    return filteredRows;
  }, [
    columnUids.checkInTime,
    columnUids.checkOutTime,
    filteredRows,
    groupViewMode
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(groupedSourceRows.length / pageSize)
  );

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return groupedSourceRows.slice(start, start + pageSize);
  }, [currentPage, groupedSourceRows, pageSize]);

  const groupedRows = useMemo(() => {
    const groups = new Map<string, any[]>();

    paginatedRows.forEach((row: any) => {
      const key =
        formatCellValue.formatTimeStampCellValue(
          row?.[columnUids.checkInTime] || row?.[columnUids.checkOutTime],
          "Date"
        ) || "Unknown Date";

      const current = groups.get(key) || [];
      current.push(row);
      groups.set(key, current);
    });

    return Array.from(groups.entries()).map(([label, items]) => ({
      label,
      items
    }));
  }, [columnUids.checkInTime, columnUids.checkOutTime, paginatedRows]);

  useEffect(() => {
    setCollapsedGroups(previousState => {
      const nextState: Record<string, boolean> = {};

      groupedRows.forEach(group => {
        nextState[group.label] = previousState[group.label] ?? false;
      });

      return nextState;
    });
  }, [groupedRows]);

  return (
    <div className=" w-full bg-[#F8FAFC] px-2 pt-2">
      <div className="flex h-[calc(100vh-0px)] min-h-0 flex-col overflow-hidden rounded-xl bg-transparent">
        <div className="flex flex-none items-center justify-between px-6 pb-3 pt-5">
          <div className="relative flex items-center gap-3">
            <button type="button" onClick={() => setIsFilterOpen(true)}>
              <IoFilterSharp className="text-lg" />
            </button>
            <div>
              <h2 className="text-[20px] font-medium text-default-800">
                {`${reportdata?.header || "Report"} ( ${activeReport?.menu || "Table Card"} )`}
              </h2>
              {activeReport?.subHeader && (
                <p className="mt-1 text-xs text-default-500">
                  {activeReport.subHeader}
                </p>
              )}
            </div>

            {isFilterOpen && (
              <div className="absolute left-0 top-12 z-20 rounded-2xl border border-gray-200 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.12)]">
                <div className="flex items-center justify-end px-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="text-default-700"
                  >
                    <RiCloseLine className="text-2xl" />
                  </button>
                </div>
                <ReportFilter
                  onSubmit={handleFilterSubmit}
                  activeFilters={activeReport?.filters || []}
                  oldFilter={reportFilters}
                  innerFilters={activeReport?.innerFilters}
                />
              </div>
            )}
          </div>
          <div />
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 py-2">
          <div className="mb-2 flex flex-none flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {groupTabs.map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    setGroupViewMode(option.id as VisitorGroupViewMode);
                    setCurrentPage(1);
                  }}
                  className={
                    groupViewMode === option.id
                      ? "flex items-center gap-2 rounded-full border border-[#111827] bg-[#111827] px-3 py-1.5 text-xs font-medium text-white"
                      : "flex items-center gap-2 rounded-full border border-[#dbe3ef] bg-white px-3 py-1.5 text-xs font-medium text-default-600"
                  }
                >
                  <span>{option.label}</span>
                  <span
                    className={
                      groupViewMode === option.id
                        ? "text-xs font-semibold text-white"
                        : "text-xs font-semibold text-[#3F6FB5]"
                    }
                  >
                    {option.count}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search"
                  className="h-9 w-56 rounded-lg border border-gray-200 pl-9 pr-3 text-xs text-default-700 placeholder:text-default-400 focus:border-gray-400 focus:outline-none"
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <svg
                  viewBox="0 0 24 24"
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-default-400"
                >
                  <path
                    d="M21 21l-4.3-4.3M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <button
                type="button"
                className="cursor-pointer px-2 disabled:opacity-40"
                onClick={() =>
                  exportTableCardData({
                    activeReport,
                    downloadData,
                    columnUids,
                    exportColumnNames,
                    getVisitorTypeLabel,
                    getPickedUpStudentsSingleLine,
                    getPickedUpStudentsImageLinks
                  })
                }
                disabled={downloadData.length === 0}
              >
                <DownloadIcon />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden rounded-2xl bg-[#F8FAFC]">
            <div className="h-full overflow-auto p-3 md:p-4">
              {loading ? (
                <div className="flex min-h-[240px] items-center justify-center">
                  <LoadingOverlay />
                </div>
              ) : paginatedRows.length > 0 ? (
                <div className="space-y-5">
                  {groupedRows.map(group => (
                    <div key={group.label} className="space-y-3">
                      <button
                        type="button"
                        onClick={() =>
                          setCollapsedGroups(previousState => ({
                            ...previousState,
                            [group.label]: !previousState[group.label]
                          }))
                        }
                      >
                        <div className="flex items-center gap-2">
                          <span className="flex items-center justify-center">
                            {collapsedGroups[group.label] ? (
                              <LuChevronRight className="h-4 w-4 text-default-600" />
                            ) : (
                              <LuChevronDown className="h-4 w-4 text-default-600" />
                            )}
                          </span>
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-default-700">
                              {group.label}
                            </h3>
                            <span className="flex h-6 min-w-6 items-center justify-center rounded-full border border-[#D8DEE8] bg-[#EEF2F6] px-2 text-xs font-medium text-default-500">
                              {group.items.length}
                            </span>
                          </div>
                        </div>
                      </button>
                      {!collapsedGroups[group.label] && (
                        <div className="grid grid-cols-4 gap-4">
                          {group.items.map((row: any) => (
                            <VisitorDataCard
                              key={`${row?.[columnUids.visitorName]}-${
                                row?.[columnUids.visitorPhoneNumber]
                              }-${row?.[columnUids.checkInTime]}`}
                              row={row}
                              columnUids={columnUids}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : searchQuery.trim() ? (
                <div className="flex min-h-[300px] w-full flex-col items-center justify-center text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-200">
                    <AiOutlineFileSearch size={40} />
                  </div>

                  <h2 className="text-xl font-semibold text-default-800">
                    No matching visitors found
                  </h2>

                  <p className="mt-2 text-sm text-default-500">
                    Try a different visitor name, student, phone number, or
                    reason.
                  </p>
                </div>
              ) : groupedSourceRows.length === 0 && filteredRows.length > 0 ? (
                <div className="flex min-h-[240px] w-full flex-col items-center justify-center pt-7 text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-200">
                    <AiOutlineFileSearch size={40} />
                  </div>
                  <h2 className="font-semibold text-default-700">
                    No visitors in this group
                  </h2>
                  <p className="mt-1 text-sm text-default-500">
                    Change the group option to see other visitor records.
                  </p>
                </div>
              ) : (
                <div className="flex min-h-[240px] w-full flex-col items-center justify-center pt-7 text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-200">
                    <AiOutlineFileSearch size={40} />
                  </div>
                  <h2 className="font-semibold text-default-700">
                    No record found
                  </h2>
                  <p className="mt-1text-default-500">
                    Please adjust your filters or date range to view report
                    data.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="z-20 mt-1 flex flex-none flex-wrap items-center justify-end gap-4 border-t border-gray-200 bg-[#F8FAFC] px-1 py-2">
            <div className="flex items-center gap-2 text-xs text-default-600">
              <span>Page Size</span>
              <select
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-8 rounded-lg border bg-white px-2 text-xs text-default-700"
              >
                {[10, 20, 50].map(size => (
                  <option key={size}>{size}</option>
                ))}
              </select>
              <span>
                {groupedSourceRows.length === 0
                  ? "0 of 0"
                  : `${(currentPage - 1) * pageSize + 1} - ${Math.min(currentPage * pageSize, groupedSourceRows.length)} of ${groupedSourceRows.length}`}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
                className="h-8 w-8 rounded-lg text-xs text-default-600 hover:bg-gray-50 disabled:opacity-30"
              >
                |&lt;
              </button>
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(page => Math.max(page - 1, 1))}
                className="h-8 w-8 rounded-lg text-xs text-default-600 hover:bg-gray-50 disabled:opacity-30"
              >
                &lt;
              </button>
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage(page => Math.min(page + 1, totalPages))
                }
                className="h-8 w-8 rounded-lg text-xs text-default-600 hover:bg-gray-50 disabled:opacity-30"
              >
                &gt;
              </button>
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
                className="h-8 w-8 rounded-lg text-xs text-default-600 hover:bg-gray-50 disabled:opacity-30"
              >
                &gt;|
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
